'use client';

import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * "N handled by others — show / hide".
 *
 * A **show/hide affordance, not a progress statement.** It says how many rows are
 * someone else's work and lets the reader fold them away; it deliberately says
 * nothing about how the stage is counted or how far along it is — that copy is
 * VALE-840's, and writing it here from a spec rather than from shipped behaviour
 * is how two definitions of the same count start drifting.
 *
 * **The default is the caller's**, because the two surfaces genuinely differ:
 * Inventory is a records surface and defaults to *shown* (EH-DI-003) — it must
 * show every row unless the reader asks otherwise — while the Discovery dashboard
 * folds them by default, which is what its "N handled by others — show" copy
 * describes. The count stays visible either way, so nothing is ever silently
 * absent.
 *
 * **Two appearances, because the two homes are different kinds of place**
 * (VALE-1760). Discovery and Redemption put this inline in the list body, as the
 * header of the fold it opens, where a bare text link is the right weight and an
 * outlined button would read as a third action competing with the rows. Inventory
 * puts it in a toolbar of outlined filter buttons, where the same text link read
 * as a stray caption wedged against the primary *Add Asset* — so there it takes
 * the filters' own shape, and sits beside them rather than beside the actions.
 */
export function HandledByOthersToggle({
  count,
  shown,
  onToggle,
  appearance = 'inline'
}: {
  count: number;
  shown: boolean;
  onToggle: () => void;
  /** `'inline'` for a fold header in a list body; `'toolbar'` beside filters. */
  appearance?: 'inline' | 'toolbar';
}) {
  if (count === 0) return null;

  const label = `${count} handled by others`;
  /*
    The same Eye / EyeOff pair in both appearances, because it carries the whole
    of what the control does — the eye is open when the rows are on screen and
    struck through when they are folded away. The inline form used to say it in
    a "(show)" / "(hide)" parenthetical instead, which read as a caption rather
    than a control and gave a full-width grey strip nothing to be recognised by
    (VALE-1802).
  */
  const Icon = shown ? Eye : EyeOff;

  if (appearance === 'toolbar') {
    return (
      <Button
        variant="outline"
        onClick={onToggle}
        /*
          Tinted while HIDING, matching what `BalanceSheetFilters` does to Type
          and Status when they are narrowing the list. Hiding rows is the state
          worth flagging — it is the one where the list is not showing
          everything — and borrowing the filter bar's own signal for it means a
          reader learns the convention once.
        */
        className={cn(!shown && 'border-info/40 bg-info-subtle')}
        aria-pressed={!shown}
        data-testid="handled-by-others-toggle"
      >
        <Icon className="mr-2 h-4 w-4" />
        {label}
      </Button>
    );
  }

  return (
    <Button
      tone="muted"
      pad="none"
      density="micro"
      variant="ghost"
      size="auto"
      gap="standard"
      onClick={onToggle}
      aria-pressed={!shown}
      data-testid="handled-by-others-toggle"
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {label}
    </Button>
  );
}
